import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, catchError, finalize, of, switchMap, timeout } from 'rxjs';
import { MaintenancePart, MaintenanceSchedulingService } from './maintenance-scheduling.service';

@Component({
  selector: 'app-maintenance-parts',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './maintenance-parts.view.html',
  styleUrls: ['./maintenance-parts.view.css']
})
export class MaintenancePartsComponent implements OnInit, OnDestroy {
  private readonly loadTimeoutMs = 15000;
  private readonly loadGuardPaddingMs = 500;
  private loadGuardTimer: ReturnType<typeof setTimeout> | null = null;
  ownerId = '';
  vehicleId: number | undefined;
  mechanicName = '';
  isMechanicReview = false;
  parts: MaintenancePart[] = [];
  loading = false;
  errorMessage = '';
  infoMessage = '';
  private routeSub: Subscription | null = null;
  private partsSub: Subscription | null = null;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly schedulingService: MaintenanceSchedulingService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe((params) => {
      this.ownerId = (params.get('ownerId') ?? '').trim();
      const rawVehicleId = params.get('vehicleId');
      const parsedVehicleId = rawVehicleId ? Number.parseInt(rawVehicleId, 10) : Number.NaN;
      this.vehicleId = Number.isFinite(parsedVehicleId) ? parsedVehicleId : undefined;
      this.mechanicName = (params.get('mechanic') ?? '').trim();
      this.isMechanicReview = (params.get('source') ?? '').trim().toLowerCase() === 'reservations';
      this.loadParts();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.partsSub?.unsubscribe();
    this.clearLoadGuard();
  }

  goBack(): void {
    if (this.isMechanicReview) {
      const queryParams: Record<string, string> = {};
      if (this.mechanicName) {
        queryParams['mechanic'] = this.mechanicName;
      }
      this.router.navigate(['/maintenance-reservations'], { queryParams });
      return;
    }

    this.router.navigate(['/dashboard'], {
      queryParams: this.dashboardQueryParams()
    });
  }

  openPart(part: MaintenancePart): void {
    if (this.isMechanicReview) {
      return;
    }

    this.router.navigate(['/schedule-maintenance'], {
      queryParams: {
        part: part.name,
        ...this.dashboardQueryParams()
      }
    });
  }

  private loadParts(): void {
    this.partsSub?.unsubscribe();
    this.errorMessage = '';
    this.infoMessage = '';

    if (this.vehicleId === undefined) {
      this.parts = this.schedulingService.getFallbackPartsSnapshot();
      this.loading = false;
      this.infoMessage = 'Showing preset maintenance defaults.';
      return;
    }

    this.loading = true;
    this.startLoadGuard();

    this.partsSub = this.schedulingService
      .getDashboardSeedParts(this.ownerId, this.vehicleId)
      .pipe(
        timeout(this.loadTimeoutMs),
        catchError(() => of([] as MaintenancePart[])),
        switchMap((seedParts) => {
          this.loading = false;
          if (seedParts.length > 0) {
            this.parts = seedParts;
            this.infoMessage = 'Refreshing maintenance parts...';
          } else if (!this.errorMessage && !this.infoMessage) {
            this.infoMessage = 'Refreshing maintenance parts...';
          }

          return this.schedulingService.getParts(this.ownerId, this.vehicleId).pipe(
            timeout(this.loadTimeoutMs),
            catchError(() => {
              if (seedParts.length > 0) {
                this.infoMessage = 'Showing available maintenance data.';
                return of(seedParts);
              }

              if (this.parts.length > 0) {
                this.infoMessage = 'Showing last loaded maintenance data.';
                return of(this.parts);
              }

              this.infoMessage = 'Showing preset maintenance defaults.';
              return of(this.schedulingService.getFallbackPartsSnapshot());
            })
          );
        }),
        catchError(() => {
          if (this.parts.length > 0) {
            this.infoMessage = 'Showing last loaded maintenance data.';
            return of(this.parts);
          }
          this.infoMessage = 'Showing preset maintenance defaults.';
          return of(this.schedulingService.getFallbackPartsSnapshot());
        }),
        finalize(() => {
          this.clearLoadGuard();
          this.loading = false;
        })
      )
      .subscribe((parts) => {
        this.parts = parts;
        if (parts.length > 0 && this.infoMessage === 'Refreshing maintenance parts...') {
          this.infoMessage = '';
        }
        if (parts.length === 0 && !this.errorMessage && !this.infoMessage) {
          this.errorMessage = 'Unable to load maintenance parts.';
        }
      });
  }

  private startLoadGuard(): void {
    this.clearLoadGuard();
    this.loadGuardTimer = setTimeout(() => {
      if (!this.loading) {
        return;
      }

      if (!this.infoMessage) {
        this.infoMessage = this.parts.length > 0
          ? 'Showing last loaded maintenance data.'
          : 'Unable to load maintenance parts.';
      }

      this.loading = false;
    }, this.loadTimeoutMs + this.loadGuardPaddingMs);
  }

  private clearLoadGuard(): void {
    if (this.loadGuardTimer !== null) {
      clearTimeout(this.loadGuardTimer);
      this.loadGuardTimer = null;
    }
  }

  private dashboardQueryParams(): Record<string, string | number> {
    const queryParams: Record<string, string | number> = {};
    if (this.ownerId) {
      queryParams['ownerId'] = this.ownerId;
    }
    if (this.vehicleId !== undefined) {
      queryParams['vehicleId'] = this.vehicleId;
    }
    return queryParams;
  }
}
